import { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  serverTimestamp,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import Swal from "sweetalert2";
import { db, storage } from "../../firebase";

const ROLE_FALLBACKS = {
  librarian: {
    roleKey: "librarian",
    roleLabel: "Бібліотекар",
    title: "Анкета бібліотекаря",
    description:
      "Заповніть форму, щоб адміністратор міг перевірити дані та змінити тип вашого кабінету.",
    warningText:
      "У разі якщо ви не заповните форму, заявка на зміну кабінету розглядатись не буде.",
    bannerUrl: "",
    isActive: true,
    fields: [
      {
        id: "library_name",
        type: "text",
        label: "Назва бібліотеки",
        placeholder: "Введіть назву бібліотеки",
        helpText: "",
        required: true,
        order: 1,
        options: [],
      },
      {
        id: "responsible_name",
        type: "text",
        label: "ФІО відповідального",
        placeholder: "Введіть ПІБ",
        helpText: "Відповідальним може бути будь-який працівник бібліотеки.",
        required: true,
        order: 2,
        options: [],
      },
      {
        id: "position",
        type: "text",
        label: "Посада",
        placeholder: "Вкажіть посаду",
        helpText: "",
        required: false,
        order: 3,
        options: [],
      },
      {
        id: "phone",
        type: "phone",
        label: "Телефон",
        placeholder: "+380...",
        helpText: "",
        required: true,
        order: 4,
        options: [],
      },
      {
        id: "email",
        type: "email",
        label: "Email",
        placeholder: "example@gmail.com",
        helpText: "",
        required: true,
        order: 5,
        options: [],
      },
      {
        id: "library_address",
        type: "textarea",
        label: "Адреса бібліотеки / контактна інформація",
        placeholder: "Вкажіть адресу, місто, область та інші контакти",
        helpText: "",
        required: true,
        order: 6,
        options: [],
      },
      {
        id: "social_links",
        type: "textarea",
        label: "Соціальні мережі або сайт бібліотеки",
        placeholder: "Instagram, Facebook, сайт або інші посилання",
        helpText: "",
        required: false,
        order: 7,
        options: [],
      },
      {
        id: "additional_info",
        type: "textarea",
        label: "Додаткова інформація або прохання",
        placeholder: "Напишіть важливу інформацію для адміністратора",
        helpText: "",
        required: false,
        order: 8,
        options: [],
      },
    ],
  },
  maister: {
    roleKey: "maister",
    roleLabel: "Майстер",
    title: "Анкета майстра",
    description:
      "Заповніть форму, щоб ми могли розглянути вашу заявку на кабінет Майстра.",
    warningText:
      "У разі якщо ви не заповните форму, заявка на зміну кабінету розглядатись не буде.",
    bannerUrl: "",
    isActive: true,
    fields: [
      {
        id: "full_name",
        type: "text",
        label: "ФІО",
        placeholder: "Введіть ваше повне імʼя",
        helpText: "",
        required: true,
        order: 1,
        options: [],
      },
      {
        id: "short_name",
        type: "text",
        label: "Як коротко вас представляти",
        placeholder: "Наприклад: Марія, дитяча психологиня",
        helpText: "",
        required: false,
        order: 2,
        options: [],
      },
      {
        id: "phone",
        type: "phone",
        label: "Телефон",
        placeholder: "+380...",
        helpText: "",
        required: true,
        order: 3,
        options: [],
      },
      {
        id: "email",
        type: "email",
        label: "Email",
        placeholder: "example@gmail.com",
        helpText: "",
        required: true,
        order: 4,
        options: [],
      },
      {
        id: "about_master",
        type: "textarea",
        label: "Про Майстра",
        placeholder: "Розкажіть про себе, досвід, діяльність, експертність",
        helpText: "",
        required: true,
        order: 5,
        options: [],
      },
      {
        id: "key_idea",
        type: "textarea",
        label: "Ключова думка / позиція",
        placeholder: "Що важливо знати про вас як про Майстра?",
        helpText: "",
        required: false,
        order: 6,
        options: [],
      },
      {
        id: "social_links",
        type: "textarea",
        label: "Соціальні мережі, сайт або портфоліо",
        placeholder: "Додайте посилання",
        helpText: "",
        required: false,
        order: 7,
        options: [],
      },
      {
        id: "video_link",
        type: "url",
        label: "Посилання на відео, якщо є",
        placeholder: "https://...",
        helpText: "",
        required: false,
        order: 8,
        options: [],
      },
    ],
  },
  business: {
    roleKey: "business",
    roleLabel: "Бізнес-партнер",
    title: "Анкета бізнес-партнера",
    description:
      "Заповніть форму, щоб адміністратор міг перевірити дані компанії та надати доступ до кабінету бізнес-партнера.",
    warningText:
      "У разі якщо ви не заповните форму, заявка на зміну кабінету розглядатись не буде.",
    bannerUrl: "",
    isActive: true,
    fields: [
      {
        id: "company_name",
        type: "text",
        label: "Назва компанії / ФОП",
        placeholder: "Введіть назву",
        helpText: "",
        required: true,
        order: 1,
        options: [],
      },
      {
        id: "contact_person",
        type: "text",
        label: "Контактна особа",
        placeholder: "ПІБ відповідальної особи",
        helpText: "",
        required: true,
        order: 2,
        options: [],
      },
      {
        id: "phone",
        type: "phone",
        label: "Телефон",
        placeholder: "+380...",
        helpText: "",
        required: true,
        order: 3,
        options: [],
      },
      {
        id: "email",
        type: "email",
        label: "Email",
        placeholder: "example@gmail.com",
        helpText: "",
        required: true,
        order: 4,
        options: [],
      },
      {
        id: "business_type",
        type: "select",
        label: "Тип бізнесу",
        placeholder: "Оберіть тип",
        helpText: "",
        required: true,
        order: 5,
        options: ["Книгарня", "Інтернет-магазин", "Школа", "Дитячий центр", "Інше"],
      },
      {
        id: "address",
        type: "textarea",
        label: "Адреса / місто / область",
        placeholder: "Вкажіть адресу або регіон роботи",
        helpText: "",
        required: false,
        order: 6,
        options: [],
      },
      {
        id: "comment",
        type: "textarea",
        label: "Коментар для адміністратора",
        placeholder: "Додайте деталі співпраці або запитання",
        helpText: "",
        required: false,
        order: 7,
        options: [],
      },
    ],
  },
};

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,.55)",
    zIndex: 9999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
  },
  modal: {
    width: "min(920px, 100%)",
    maxHeight: "92vh",
    overflow: "auto",
    background: "#fff",
    borderRadius: 16,
    boxShadow: "0 20px 60px rgba(0,0,0,.24)",
  },
  head: {
    padding: "22px 24px",
    borderBottom: "1px solid #eee",
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
  },
  body: { padding: 24 },
  title: { margin: 0, fontSize: 26, lineHeight: 1.2 },
  close: {
    border: "none",
    background: "#f1f1f1",
    borderRadius: 999,
    width: 36,
    height: 36,
    cursor: "pointer",
    fontSize: 20,
  },
  banner: {
    width: "100%",
    maxHeight: 260,
    objectFit: "cover",
    borderRadius: 12,
    marginBottom: 18,
  },
  warning: {
    background: "#fff4d6",
    border: "1px solid #ffd36b",
    borderRadius: 10,
    padding: "12px 14px",
    margin: "16px 0",
    fontWeight: 600,
  },
  field: { marginBottom: 18 },
  label: { display: "block", fontWeight: 700, marginBottom: 8 },
  input: {
    width: "100%",
    border: "1px solid #d6d6d6",
    borderRadius: 10,
    padding: "12px 14px",
    fontSize: 16,
    boxSizing: "border-box",
  },
  help: { margin: "6px 0 0", color: "#777", fontSize: 13 },
  row: { display: "flex", alignItems: "center", gap: 10, margin: "7px 0" },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    borderTop: "1px solid #eee",
    padding: 16,
  },
  submit: {
    border: "none",
    background: "#3e51c1",
    color: "#fff",
    borderRadius: 10,
    padding: "12px 18px",
    cursor: "pointer",
    fontWeight: 700,
  },
  cancel: {
    border: "1px solid #ccc",
    background: "#fff",
    borderRadius: 10,
    padding: "12px 18px",
    cursor: "pointer",
  },
};

const normalizeFields = (fields = []) =>
  [...fields]
    .filter((field) => field && field.id && field.label)
    .sort((a, b) => Number(a.order || 0) - Number(b.order || 0));

const getRoleKey = (role) => role?.key || role?.roleKey || "";

const getUserContact = (user, realUser) => ({
  email:
    user?.email ||
    realUser?.email ||
    realUser?.userEmail ||
    realUser?.mail ||
    "",
  phone:
    realUser?.phone ||
    realUser?.tel ||
    realUser?.telephone ||
    realUser?.number ||
    "",
  name:
    realUser?.displayName ||
    realUser?.name ||
    realUser?.firstName ||
    user?.displayName ||
    "",
});

const getContactFromAnswers = (answers) => {
  const phoneAnswer = answers.find((answer) => {
    const key = `${answer.fieldId} ${answer.label} ${answer.type}`.toLowerCase();
    return key.includes("phone") || key.includes("тел") || key.includes("телефон");
  });

  const emailAnswer = answers.find((answer) => {
    const key = `${answer.fieldId} ${answer.label} ${answer.type}`.toLowerCase();
    return key.includes("email") || key.includes("mail") || key.includes("пошта") || key.includes("емейл");
  });

  return {
    phoneFromForm:
      typeof phoneAnswer?.value === "string" ? phoneAnswer.value : "",
    emailFromForm:
      typeof emailAnswer?.value === "string" ? emailAnswer.value : "",
  };
};

export default function RoleChangeFormModal({
  role,
  user,
  realUser,
  onClose,
  onSuccess,
}) {
  const [form, setForm] = useState(null);
  const [values, setValues] = useState({});
  const [files, setFiles] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const roleKey = getRoleKey(role);
  const userContact = useMemo(() => getUserContact(user, realUser), [user, realUser]);

  useEffect(() => {
    let alive = true;

    const loadForm = async () => {
      setLoading(true);
      try {
        const snap = await getDoc(doc(db, "roleForms", roleKey));
        const fallback = ROLE_FALLBACKS[roleKey] || null;
        const data = snap.exists() ? { id: snap.id, ...snap.data() } : fallback;

        if (!alive) return;
        setForm(data);
      } catch (error) {
        console.error("Помилка завантаження форми зміни ролі:", error);
        if (alive) setForm(ROLE_FALLBACKS[roleKey] || null);
      } finally {
        if (alive) setLoading(false);
      }
    };

    if (roleKey) loadForm();

    return () => {
      alive = false;
    };
  }, [roleKey]);

  const fields = useMemo(() => normalizeFields(form?.fields), [form?.fields]);

  const setValue = (fieldId, value) => {
    setValues((prev) => ({ ...prev, [fieldId]: value }));
  };

  const handleCheckbox = (fieldId, option, checked) => {
    setValues((prev) => {
      const current = Array.isArray(prev[fieldId]) ? prev[fieldId] : [];
      const next = checked
        ? [...new Set([...current, option])]
        : current.filter((item) => item !== option);
      return { ...prev, [fieldId]: next };
    });
  };

  const uploadRequestFile = async (field, file) => {
    if (!file) return null;
    const safeName = String(file.name || "file").replace(/[^a-zA-Z0-9а-яА-ЯіІїЇєЄґҐ._-]/g, "_");
    const fileRef = ref(
      storage,
      `role-change-requests/${user?.uid || "unknown"}/${roleKey}/${Date.now()}-${field.id}-${safeName}`,
    );
    await uploadBytes(fileRef, file, { contentType: file.type || undefined });
    const url = await getDownloadURL(fileRef);

    return {
      name: file.name,
      size: file.size,
      type: file.type,
      url,
    };
  };

  const validate = () => {
    for (const field of fields) {
      if (!field.required) continue;

      if (field.type === "file") {
        if (!files[field.id]) return field.label;
        continue;
      }

      const value = values[field.id];
      if (Array.isArray(value) && value.length === 0) return field.label;
      if (value === undefined || value === null || String(value).trim() === "") {
        return field.label;
      }
    }

    return null;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!user?.uid) {
      Swal.fire("Увага", "Потрібно увійти в кабінет, щоб подати заявку.", "info");
      return;
    }

    const missing = validate();
    if (missing) {
      Swal.fire("Заповніть форму", `Обовʼязкове поле: ${missing}`, "info");
      return;
    }

    setSubmitting(true);

    try {
      const answers = [];
      const answersMap = {};

      for (const field of fields) {
        let value = values[field.id] ?? "";

        if (field.type === "file") {
          value = await uploadRequestFile(field, files[field.id]);
        }

        answers.push({
          fieldId: field.id,
          label: field.label,
          type: field.type || "text",
          value,
        });
        answersMap[field.id] = value;
      }

      const formContacts = getContactFromAnswers(answers);
      const payload = {
        userId: user.uid,
        userEmail: userContact.email,
        userPhone: userContact.phone,
        userName: userContact.name,
        currentRole: realUser?.category || "",
        requestedRoleKey: roleKey,
        requestedRoleLabel: form?.roleLabel || role?.label || "",
        formId: roleKey,
        formTitle: form?.title || role?.label || "Форма зміни кабінету",
        status: "pending",
        answers,
        answersMap,
        contacts: {
          emailFromAccount: userContact.email,
          phoneFromAccount: userContact.phone,
          emailFromForm: formContacts.emailFromForm,
          phoneFromForm: formContacts.phoneFromForm,
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const docRef = await addDoc(collection(db, "roleChangeRequests"), payload);

      const localRequest = {
        id: docRef.id,
        type: payload.requestedRoleLabel,
        key: roleKey,
        ts: Date.now(),
      };

      Swal.fire({
        icon: "success",
        title: "Заявку відправлено",
        text: "Адміністратор перегляне відповіді та змінить кабінет після перевірки.",
      });

      if (onSuccess) onSuccess(localRequest);
    } catch (error) {
      console.error("Помилка відправки заявки на зміну ролі:", error);
      Swal.fire("Помилка", "Не вдалося відправити заявку. Спробуйте ще раз.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    const commonProps = {
      id: field.id,
      style: styles.input,
      value: values[field.id] || "",
      required: Boolean(field.required),
      placeholder: field.placeholder || "",
      onChange: (event) => setValue(field.id, event.target.value),
    };

    if (field.type === "textarea") {
      return <textarea {...commonProps} rows={5} />;
    }

    if (field.type === "select") {
      return (
        <select {...commonProps}>
          <option value="">Оберіть варіант</option>
          {(field.options || []).map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      );
    }

    if (field.type === "radio") {
      return (
        <div>
          {(field.options || []).map((option) => (
            <label key={option} style={styles.row}>
              <input
                type="radio"
                name={field.id}
                value={option}
                checked={values[field.id] === option}
                onChange={() => setValue(field.id, option)}
              />
              <span>{option}</span>
            </label>
          ))}
        </div>
      );
    }

    if (field.type === "checkbox") {
      const selected = Array.isArray(values[field.id]) ? values[field.id] : [];
      return (
        <div>
          {(field.options || []).map((option) => (
            <label key={option} style={styles.row}>
              <input
                type="checkbox"
                checked={selected.includes(option)}
                onChange={(event) => handleCheckbox(field.id, option, event.target.checked)}
              />
              <span>{option}</span>
            </label>
          ))}
        </div>
      );
    }

    if (field.type === "file") {
      return (
        <input
          id={field.id}
          style={styles.input}
          type="file"
          onChange={(event) =>
            setFiles((prev) => ({ ...prev, [field.id]: event.target.files?.[0] || null }))
          }
        />
      );
    }

    const inputType =
      field.type === "email"
        ? "email"
        : field.type === "phone"
          ? "tel"
          : field.type === "url"
            ? "url"
            : field.type === "number"
              ? "number"
              : "text";

    return <input {...commonProps} type={inputType} />;
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(event) => event.stopPropagation()}>
        <div style={styles.head}>
          <div>
            <h2 style={styles.title}>{form?.title || role?.label || "Форма"}</h2>
            {form?.description ? <p>{form.description}</p> : null}
          </div>
          <button type="button" style={styles.close} onClick={onClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={styles.body}>
            {loading ? <p>Завантаження форми...</p> : null}

            {!loading && !form ? (
              <p>Форму для цієї ролі ще не створено в адмінці.</p>
            ) : null}

            {form?.bannerUrl ? (
              <img src={form.bannerUrl} alt={form.title || "Банер форми"} style={styles.banner} />
            ) : null}

            {form?.warningText ? <div style={styles.warning}>{form.warningText}</div> : null}

            {form && form.isActive === false ? (
              <div style={styles.warning}>Ця форма тимчасово неактивна. Заявку зараз подати не можна.</div>
            ) : null}

            {form && form.isActive !== false && fields.map((field) => (
              <div key={field.id} style={styles.field}>
                <label htmlFor={field.id} style={styles.label}>
                  {field.label} {field.required ? <span style={{ color: "#c00" }}>*</span> : null}
                </label>
                {renderField(field)}
                {field.helpText ? <p style={styles.help}>{field.helpText}</p> : null}
              </div>
            ))}
          </div>

          <div style={styles.actions}>
            <button type="button" style={styles.cancel} onClick={onClose} disabled={submitting}>
              Закрити
            </button>
            <button
              type="submit"
              style={styles.submit}
              disabled={submitting || loading || !form || form?.isActive === false}
            >
              {submitting ? "Відправляємо..." : "Відправити заявку"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
